import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AgentNotification } from '../types';
import soundService from '../services/soundService';

interface NotificationContextType {
  notifications: AgentNotification[];
  visibleNotifications: AgentNotification[];
  addNotification: (notification: Omit<AgentNotification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  snoozeNotification: (id: string, minutes: number) => void;
  showAllNotifications: () => void;
  executeNotificationAction: (notification: AgentNotification) => void;
  hasHiddenNotifications: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  onNavigate?: (action: string, data?: any) => void;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  onNavigate 
}) => {
  const [notifications, setNotifications] = useState<AgentNotification[]>([]);

  // Calculate visible notifications (not snoozed or hidden)
  const visibleNotifications = notifications.filter(notification => {
    // Check if notification is explicitly hidden
    if (notification.isVisible === false) return false;
    
    // Check if notification is snoozed and still within snooze period
    if (notification.snoozeUntil) {
      const snoozeTime = new Date(notification.snoozeUntil);
      const now = new Date();
      return now >= snoozeTime;
    }
    
    return true;
  });

  // Check if there are hidden notifications
  const hasHiddenNotifications = notifications.length > visibleNotifications.length;

  const addNotification = useCallback((notificationData: Omit<AgentNotification, 'id' | 'timestamp'>) => {
    // Create unique key for deduplication
    const notificationKey = (notificationData as any).notificationKey || 
      `${notificationData.type}_${(notificationData as any).data?.ticketId || Date.now()}`;

    console.log('📥 Frontend received notification:', {
      type: notificationData.type,
      title: notificationData.title,
      providedKey: (notificationData as any).notificationKey,
      finalKey: notificationKey,
      ticketId: (notificationData as any).data?.ticketId
    });

    // Check if notification with same key already exists
    setNotifications(prev => {
      console.log('🔍 DEDUPLICATION CHECK:', {
        notificationKey,
        currentNotificationCount: prev.length,
        existingKeys: prev.map(n => (n as any).notificationKey),
        newType: notificationData.type
      });

      // Smart deduplication: only for recurring notification types
      if (notificationKey && (notificationData.type === 'escalation' || notificationData.type === 'unassigned_ticket')) {
        const keyExists = prev.find(n => (n as any).notificationKey === notificationKey);
        if (keyExists) {
          console.log(`🔕 Duplicate periodic notification blocked by key: ${notificationKey}`, {
            existingNotification: keyExists,
            blockedNotification: notificationData
          });
          console.log('🔇 Sound will NOT play for duplicate periodic notification');
          return prev; // Don't add duplicate periodic notifications
        }
      }

      // For other notification types, check by ticketId and type but be more lenient
      if (notificationData.type !== 'escalation' && notificationData.type !== 'unassigned_ticket') {
        const existingIndex = prev.findIndex(n => 
          (n.type === notificationData.type && 
           JSON.stringify((n as any).data?.ticketId) === JSON.stringify((notificationData as any).data?.ticketId) &&
           Math.abs(new Date(n.timestamp).getTime() - Date.now()) < 5000) // Only consider duplicates if within 5 seconds
        );

        if (existingIndex !== -1) {
          console.log(`🔕 Recent duplicate notification blocked - key: ${notificationKey}`, {
            existingNotification: prev[existingIndex],
            blockedNotification: notificationData
          });
          return prev; // Don't add recent duplicate
        }
      }

      // Set duration based on notification type if not explicitly provided
      let autoDismissDuration = notificationData.duration;
      if (!autoDismissDuration) {
        switch (notificationData.type) {
          case 'welcome':
            autoDismissDuration = 5000; // 5 seconds for welcome notifications
            break;
          case 'agent_status':
            autoDismissDuration = 8000; // 8 seconds for agent online/offline notifications
            break;
          default:
            autoDismissDuration = undefined; // No auto-dismiss for other types
        }
      }

      const notification: AgentNotification = {
        ...notificationData,
        id: (notificationData as any).id || `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        duration: autoDismissDuration,
        isVisible: true,
        snoozeCount: 0,
        ...(notificationKey && { notificationKey } as any),
      };

      // Play sound if enabled (only for new notifications)
      if (notification.sound !== false) {
        console.log('🔊 Playing notification sound for:', notification.title);
        playNotificationSound(notification.priority);
      }

      console.log(`✅ New notification added - key: ${notificationKey}, type: ${notification.type}, auto-dismiss: ${autoDismissDuration ? `${autoDismissDuration}ms` : 'disabled'}`);
      console.log('📊 Notification list after addition:', {
        totalNotifications: prev.length + 1,
        newNotification: {
          id: notification.id,
          title: notification.title,
          type: notification.type,
          duration: notification.duration,
          notificationKey: (notification as any).notificationKey
        }
      });
      
      const updatedList = [notification, ...prev];
      console.log('📋 Final notification list:', updatedList.map(n => ({ id: n.id, title: n.title, type: n.type })));
      
      // Set up auto-dismiss timer for notifications with duration
      if (autoDismissDuration && autoDismissDuration > 0) {
        console.log(`⏰ Setting auto-dismiss timer for ${autoDismissDuration}ms for notification: ${notification.title}`);
        setTimeout(() => {
          console.log(`⏰ Auto-dismissing notification after ${autoDismissDuration}ms: ${notification.title}`);
          removeNotification(notification.id);
        }, autoDismissDuration);
      }
      
      return updatedList;
    });
  }, []);

  const removeNotification = useCallback(async (id: string) => {
    // Find the notification to get its details
    const notification = notifications.find(n => n.id === id);
    
    try {
      // Call API to dismiss notification permanently with notification details
      const response = await fetch(`/api/notifications/${id}/dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notificationKey: (notification as any)?.notificationKey,
          title: notification?.title,
          type: notification?.type
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Notification dismissed permanently:', result);
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== id));
      } else {
        console.error('❌ Failed to dismiss notification:', await response.text());
        // Still remove from local state as fallback
        setNotifications(prev => prev.filter(n => n.id !== id));
      }
    } catch (error) {
      console.error('❌ Error dismissing notification:', error);
      // Still remove from local state as fallback
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  }, [notifications]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const snoozeNotification = useCallback(async (id: string, minutes: number) => {
    try {
      // Call API to snooze notification
      const response = await fetch(`/api/notifications/${id}/snooze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ minutes })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Notification snoozed successfully until:', result.snoozeUntil);
        
        // Update local state
        setNotifications(prev => prev.map(notification => 
          notification.id === id 
            ? { 
                ...notification, 
                snoozeUntil: result.snoozeUntil,
                snoozeCount: (notification.snoozeCount || 0) + 1
              }
            : notification
        ));
      } else {
        console.error('❌ Failed to snooze notification:', await response.text());
        // Fallback to local snooze
        const snoozeUntil = new Date();
        snoozeUntil.setMinutes(snoozeUntil.getMinutes() + minutes);
        
        setNotifications(prev => prev.map(notification => 
          notification.id === id 
            ? { 
                ...notification, 
                snoozeUntil: snoozeUntil.toISOString(),
                snoozeCount: (notification.snoozeCount || 0) + 1
              }
            : notification
        ));
      }
    } catch (error) {
      console.error('❌ Error snoozing notification:', error);
      // Fallback to local snooze
      const snoozeUntil = new Date();
      snoozeUntil.setMinutes(snoozeUntil.getMinutes() + minutes);
      
      setNotifications(prev => prev.map(notification => 
        notification.id === id 
          ? { 
              ...notification, 
              snoozeUntil: snoozeUntil.toISOString(),
              snoozeCount: (notification.snoozeCount || 0) + 1
            }
          : notification
      ));
    }
  }, []);

  const showAllNotifications = useCallback(() => {
    setNotifications(prev => prev.map(notification => ({
      ...notification,
      isVisible: true,
      snoozeUntil: undefined
    })));
  }, []);

  const playNotificationSound = (priority: AgentNotification['priority']) => {
    try {
      switch (priority) {
        case 'urgent':
          soundService.playRedTicketSound();
          break;
        case 'high':
          soundService.playYellowTicketSound();
          break;
        case 'medium':
          soundService.playTicketUpdateSound();
          break;
        case 'low':
          soundService.playNewTicketSound();
          break;
        default:
          soundService.playNewTicketSound();
      }
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  const executeNotificationAction = useCallback((notification: AgentNotification) => {
    if (!notification.actionButton) return;

    const { action, data } = notification.actionButton;

    // Remove the notification when action is executed
    removeNotification(notification.id);

    // Execute the action
    if (onNavigate) {
      onNavigate(action, data);
    }

    // Default action handlers if no custom navigator is provided
    // Note: The App component should provide onNavigate, but this is a fallback
    console.log('⚠️ Using fallback navigation for action:', action, data);
  }, [onNavigate, removeNotification]);

  // Auto-restore snoozed notifications
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setNotifications(prev => prev.map(notification => {
        if (notification.snoozeUntil) {
          const snoozeTime = new Date(notification.snoozeUntil);
          if (now >= snoozeTime) {
            // Remove snooze time to make notification visible again
            const { snoozeUntil, ...notificationWithoutSnooze } = notification;
            return notificationWithoutSnooze;
          }
        }
        return notification;
      }));
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllNotifications();
    };
  }, [clearAllNotifications]);

  const value: NotificationContextType = {
    notifications,
    visibleNotifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    snoozeNotification,
    showAllNotifications,
    executeNotificationAction,
    hasHiddenNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 